
import { Message } from '@/types/chat';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { differenceInDays } from 'date-fns';
import { UserType } from '@/types/user';

/**
 * Hook for handling subscription-related checks and messaging
 */
export const useSubscriptionChecks = () => {
  /**
   * Displays no reports remaining message
   */
  const handleNoReportsRemaining = (setMessages: (updater: (prev: Message[]) => Message[]) => void) => {
    setMessages(prev => [...prev, {
      role: 'assistant' as const,
      content: "You've used all your reports for this month. Please subscribe to a paid plan to continue generating reports."
    }]);
  };

  /**
   * Displays report limit reached message
   */
  const handleFreeReportUsed = (setMessages: (updater: (prev: Message[]) => Message[]) => void) => {
    setMessages(prev => [...prev, {
      role: 'assistant' as const,
      content: "You've reached your monthly report limit. To generate more reports, please select a paid subscription plan."
    }]);
  };

  /**
   * Prompts user to upgrade their plan with a custom message
   */
  const promptForUpgrade = (message: string, setMessages: (updater: (prev: Message[]) => Message[]) => void) => {
    setMessages(prev => [...prev, {
      role: 'assistant' as const,
      content: message
    }]);
  };

  /**
   * Shows a toast warning about remaining reports
   */
  const showRemainingReportsWarning = (remainingReports: number, userType: string) => {
    // Ensure userType is a valid UserType
    const validUserTypes: UserType[] = ['admin', 'free', 'starter', 'pro', 'premium'];
    const effectiveUserType = validUserTypes.includes(userType as UserType) ? userType : 'free';
    
    if (effectiveUserType === 'free' && remainingReports === 1) {
      toast.warning(`You have 1 report remaining. Upgrade to continue generating reports after that.`);
    }
    else if (effectiveUserType === 'starter' && remainingReports === 1) {
      toast.warning(`You have 1 report remaining on your Starter plan. Consider upgrading to Pro for more reports.`);
    }
    else if (effectiveUserType === 'pro' && remainingReports <= 3 && remainingReports > 0) {
      toast.warning(`You have ${remainingReports} reports remaining on your Pro plan. Consider upgrading to Premium for more reports.`);
    }
    else if (effectiveUserType === 'premium' && remainingReports <= 5 && remainingReports > 0) {
      toast.warning(`You have ${remainingReports} reports remaining. Contact support@analystx.co for additional reporting needs.`);
    }
  };

  /**
   * Directly check report usage from database
   * This provides a more reliable way to enforce limits
   */
  const getDirectReportCount = async (userId: string): Promise<number> => {
    try {
      // Check if 30 days have passed since last reset
      const { data, error } = await supabase
        .from('report_usage')
        .select('reports_generated, last_reset_at')
        .eq('user_id', userId)
        .single();
        
      if (error && error.code !== 'PGRST116') {
        console.error('Error getting direct report count:', error);
        return 0;
      }
      
      // If the user doesn't have a record yet, they haven't used any reports
      if (error && error.code === 'PGRST116') {
        console.log('New user without report usage record detected');
        return 0;
      }
      
      // Check if reset is needed (30+ days since last reset)
      if (data?.last_reset_at) {
        const lastResetDate = new Date(data.last_reset_at);
        const today = new Date();
        const daysSinceReset = differenceInDays(today, lastResetDate);
        
        if (daysSinceReset >= 30) {
          console.log(`It's been ${daysSinceReset} days since the last reset. Resetting report count.`);
          
          // Reset the report count
          const { error: resetError } = await supabase
            .from('report_usage')
            .update({ 
              reports_generated: 0,
              last_reset_at: today.toISOString() 
            })
            .eq('user_id', userId);
            
          if (resetError) {
            console.error('Error resetting report count:', resetError);
          } else {
            console.log('Successfully reset report count after 30 days');
            return 0; // Return 0 since we just reset
          }
        }
      }
      
      return data?.reports_generated || 0;
    } catch (err) {
      console.error('Error in direct report count check:', err);
      return 0;
    }
  };

  /**
   * Checks subscription limits based on user type and report count
   * Returns true if user can continue, false if they need to upgrade
   */
  const checkSubscriptionLimits = async (
    userType: string | null, 
    reportCount: number | null, 
    messagesLength: number,
    usedFreeReport: boolean,
    setMessages: (updater: (prev: Message[]) => Message[]) => void,
    userEmail?: string | null,
    userId?: string | null
  ): Promise<boolean> => {
    // Admin check by email
    if (userEmail === 'kintupercy@gmail.com') {
      console.log('Admin email detected, bypassing all checks');
      return true;
    }
    
    // If we have the user ID, double check with the database directly
    // This will also handle the 30-day reset logic
    if (userId) {
      const directReportCount = await getDirectReportCount(userId);
      console.log(`Direct DB report count for ${userId}: ${directReportCount}`);
      
      // Override the provided report count with the direct count from DB
      reportCount = directReportCount;
    }
    
    // Normalize user type to one of the valid types
    const validUserTypes: UserType[] = ['admin', 'free', 'starter', 'pro', 'premium'];
    const effectiveUserType = (userType && validUserTypes.includes(userType as UserType)) ? userType : 'free';
    
    // Calculate remaining reports based on user type
    let planLimit = 2; // Default for free users
    if (effectiveUserType === 'starter') planLimit = 10;
    else if (effectiveUserType === 'pro') planLimit = 30;
    else if (effectiveUserType === 'premium') planLimit = 60;
    
    const remainingReports = reportCount !== null ? Math.max(0, planLimit - reportCount) : planLimit;
    
    // Show warning toast if appropriate
    if (remainingReports > 0) {
      showRemainingReportsWarning(remainingReports, effectiveUserType);
    }
    
    // Free user handling - all users get 2 reports per 30-day window
    if (effectiveUserType === 'free' && reportCount !== null && reportCount >= 2) {
      console.log('Free user has exceeded 2 report limit:', reportCount);
      promptForUpgrade("You've used your 2 reports for this 30-day period. Please upgrade to a paid plan for more reports.", setMessages);
      return false;
    }
    
    // Check report limits for each plan type with strict enforcement
    if (effectiveUserType === 'starter' && reportCount !== null && reportCount >= 10) {
      console.log('Starter user has reached report limit:', reportCount);
      promptForUpgrade("You've reached your limit. Upgrade to continue.", setMessages);
      return false;
    }

    if (effectiveUserType === 'pro' && reportCount !== null && reportCount >= 30) {
      console.log('Pro user has reached report limit:', reportCount);
      promptForUpgrade("You've reached your limit. Upgrade to continue.", setMessages);
      return false;
    }
    
    if (effectiveUserType === 'premium' && reportCount !== null && reportCount >= 60) {
      console.log('Premium user has reached report limit:', reportCount);
      promptForUpgrade("You've reached your monthly Premium plan limit. Please contact us at support@analystx.co for additional reporting needs.", setMessages);
      return false;
    }

    // If none of the limiting conditions are met, allow report generation
    return true;
  };

  return {
    handleNoReportsRemaining,
    handleFreeReportUsed,
    promptForUpgrade,
    checkSubscriptionLimits,
    getDirectReportCount,
    showRemainingReportsWarning
  };
};
